import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, startLifeguideHandoff } from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { Heart, ArrowLeft, Loader2, Check, MessageSquare, ArrowRight } from "lucide-react";
import { ConcernPicker, toggleConcern, SourceSelector } from "@/lib/scriptureUi";

const RELATIONSHIPS = [
  { value: "friend", label: "Friend" }, { value: "spouse", label: "Spouse" },
  { value: "child", label: "Child" }, { value: "parent", label: "Parent" },
  { value: "family", label: "Family member" }, { value: "coworker", label: "Coworker" },
  { value: "other", label: "Someone else" },
];
const FREQ = [
  { value: "morning", label: "Every morning" }, { value: "few_days", label: "Every few days" }, { value: "weekly", label: "Weekly" },
];
const SENDER = [
  { value: "name", label: "My first name" }, { value: "cares", label: "Someone who cares about you" }, { value: "anonymous", label: "Anonymous" },
];

export default function SendWisdom() {
  const [topics, setTopics] = useState([]);
  const [relationship, setRelationship] = useState("friend");
  const [concerns, setConcerns] = useState([]);
  const [mode, setMode] = useState("once");           // once | recurring
  const [frequency, setFrequency] = useState("weekly");
  const [source, setSource] = useState("proverbs");
  const [channel, setChannel] = useState("email");
  const [contact, setContact] = useState("");
  const [senderMode, setSenderMode] = useState("cares");
  const [senderName, setSenderName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/topics").then((r) => setTopics(r.data)).catch(() => {}); }, []);
  const toggle = (slug, max) => setConcerns((c) => toggleConcern(c, slug, max));

  const submit = async () => {
    if (!contact.trim()) { toast.error(channel === "email" ? "Enter their email." : "Enter their number."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/send-wisdom", {
        recipient_channel: channel, recipient_contact: contact, relationship, concerns,
        mode, frequency, source, sender_mode: senderMode, sender_name: senderName,
      });
      track("send_wisdom", { path: "/send", topic: concerns[0], channel });
      setDone(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send right now."); }
    finally { setBusy(false); }
  };

  const askLifeguide = async () => {
    track("bridge_click", { path: "/send", source: "send_followup" });
    try { const r = await startLifeguideHandoff({ topic: concerns[0] || "", context: "send" }); if (!r.ok) toast.error("Could not open LifeGuide right now."); }
    catch { toast.error("Could not open LifeGuide right now."); }
  };

  if (done) {
    return (
      <div className="max-w-md mx-auto px-5 py-20 text-center">
        <div className="mx-auto mb-6 grid place-items-center w-14 h-14 rounded-full bg-[#F1F5F1]"><Check className="w-7 h-7 text-[#2C4C3B]" /></div>
        <h1 data-testid="send-done-headline" className="font-serif-display text-3xl text-[#0F172A]">{done.coming_soon ? "Saved" : done.pending_optin ? "Almost there" : "Sent"}</h1>
        <p className="text-[#475569] mt-3 leading-relaxed">{done.message}</p>
        <div className="mt-8 rounded-2xl bg-[#F1F5F1] px-6 py-6 text-left">
          <p className="font-serif-display text-lg text-[#0F172A]">Trying to help someone you care about?</p>
          <p className="text-sm text-[#475569] mt-1">Need help figuring out what to say or how to support them? LifeGuide can help you find the right words.</p>
          <button data-testid="send-ask-lifeguide" onClick={askLifeguide} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0F172A] text-white text-sm font-semibold">Ask LifeGuide <ArrowRight className="w-4 h-4" /></button>
        </div>
        <Link to="/" data-testid="send-home-link" className="inline-block mt-8 px-6 py-3 rounded-full border border-[#2C4C3B] text-[#2C4C3B] font-medium">Back home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#2C4C3B] mb-6"><ArrowLeft className="w-4 h-4" /> Home</Link>
      <div className="flex items-center gap-2 mb-2"><Heart className="w-5 h-5 text-[#2C4C3B]" /><span className="text-xs uppercase tracking-[0.2em] text-[#B8912F] font-semibold">Free to use</span></div>
      <h1 className="font-serif-display text-4xl text-[#0F172A]">Send Wisdom to Someone</h1>
      <p className="text-[#475569] mt-2 mb-8">Sometimes the right Scripture can say what we can't.</p>

      <div className="space-y-7">
        <Block title="Who is this for?">
          <Chips testidPrefix="rel" options={RELATIONSHIPS} value={relationship} onChange={setRelationship} />
        </Block>

        <Block title="What are they facing?" hint="up to 3 (optional)">
          <div data-testid="send-concerns"><ConcernPicker topics={topics} selected={concerns} onToggle={toggle} testidPrefix="send-concern" /></div>
        </Block>

        <Block title="How would you like to encourage them?">
          <div className="flex flex-wrap gap-2">
            <Seg testid="mode-once" active={mode === "once"} onClick={() => setMode("once")}>Send one Scripture</Seg>
            <Seg testid="mode-recurring" active={mode === "recurring"} onClick={() => setMode("recurring")}>Send ongoing encouragement</Seg>
          </div>
        </Block>

        {mode === "recurring" && (
          <Block title="How often?">
            <Chips testidPrefix="freq" options={FREQ} value={frequency} onChange={setFrequency} />
            <p className="text-xs text-[#94A3B8] leading-relaxed mt-2">They'll be asked to say yes first — ongoing messages only begin once they opt in, and they can stop anytime. We never subscribe someone automatically.</p>
          </Block>
        )}

        <Block title="Which Scripture?">
          <SourceSelector value={source} onChange={setSource} testidPrefix="src" />
        </Block>

        <Block title="Send by">
          <div className="flex flex-wrap gap-2">
            <Seg testid="channel-email" active={channel === "email"} onClick={() => setChannel("email")}>Email</Seg>
            <Seg testid="channel-text" active={channel === "text"} onClick={() => setChannel("text")}><MessageSquare className="w-4 h-4 inline mr-1" /> Text</Seg>
          </div>
          <input data-testid="send-contact-input" value={contact} onChange={(e) => setContact(e.target.value)}
            type={channel === "email" ? "email" : "tel"} placeholder={channel === "email" ? "their@email.com" : "their phone number"}
            className="mt-3 w-full rounded-full border border-[#E2E8F0] px-4 py-3 text-sm focus:outline-none focus:border-[#2C4C3B]" />
          {channel === "text" && <p className="text-xs text-[#94A3B8] mt-2">They'll get one text sent from our approved number. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help.</p>}
        </Block>

        <Block title="Who should they see it's from?">
          <Chips testidPrefix="sender" options={SENDER} value={senderMode} onChange={setSenderMode} />
          {senderMode === "name" && (
            <input data-testid="sender-name-input" value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Your first name"
              className="mt-3 w-full rounded-full border border-[#E2E8F0] px-4 py-3 text-sm focus:outline-none focus:border-[#2C4C3B]" />
          )}
        </Block>

        <button data-testid="send-wisdom-submit" onClick={submit} disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#2C4C3B] text-white text-base font-semibold hover:bg-[#1E3629] transition-colors disabled:opacity-60">
          {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</> : "Send Wisdom"}
        </button>
      </div>
    </div>
  );
}

function Block({ title, hint, children }) {
  return (
    <div>
      <p className="text-sm font-semibold text-[#2C4C3B] mb-1">{title}{hint && <span className="font-normal text-[#94A3B8]"> · {hint}</span>}</p>
      <div className="mt-2">{children}</div>
    </div>
  );
}
function Chips({ options, value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o.value} data-testid={`${testidPrefix}-${o.value}`} onClick={() => onChange(o.value)}
          className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${value === o.value ? "bg-[#0F172A] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:border-[#0F172A]"}`}>{o.label}</button>
      ))}
    </div>
  );
}
function Seg({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${active ? "bg-[#2C4C3B] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:border-[#2C4C3B]"}`}>{children}</button>
  );
}
